/**
 * Contract Readiness Score Calculator
 *
 * Calculates how ready a company profile is for winning federal contracts.
 * This is DIFFERENT from profile completeness - it measures contract-specific requirements.
 *
 * Scoring breakdown:
 * - SAM.gov Registration (UEI, CAGE): 20%
 * - Set-Asides: 15%
 * - NAICS Codes: 15%
 * - Past Performance with Agencies: 15%
 * - Key Personnel with Clearances: 10%
 * - Certifications: 10%
 * - Pricing Data: 10%
 * - Compliance Documents: 5%
 */

import { Profile } from '../contexts/AuthContext';

export interface ReadinessScoreComponent {
  name: string;
  score: number;
  maxScore: number;
  weight: number;
  status: 'complete' | 'partial' | 'missing' | 'warning';
  details: string;
  actionItems: string[];
}

export interface ContractReadinessResult {
  totalScore: number;
  readinessLevel: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Not Ready';
  components: ReadinessScoreComponent[];
  criticalMissing: string[];
  recommendations: string[];
  isContractReady: boolean;
}

export interface SetAsideDocument {
  set_aside_type: string;
  document_url?: string;
  expiration_date?: string;
}

export interface PastPerformanceRecord {
  id: string;
  agency?: string;
  sector_type?: string;
  contract_value?: number;
  cpars_overall_rating?: number;
  contract_number?: string;
}

export interface ComplianceDocument {
  document_type: string;
  expiration_date?: string;
  status?: string;
}

export interface KeyPersonnelRecord {
  security_clearance?: string;
  certifications?: string[];
}

export interface PricingDataRecord {
  id: string;
  naics_codes?: string[];
}

export class ContractReadinessScore {

  /**
   * Calculate the full contract readiness score
   */
  static calculate(
    profile: Profile | null,
    setAsideDocuments: SetAsideDocument[] = [],
    pastPerformance: PastPerformanceRecord[] = [],
    keyPersonnel: KeyPersonnelRecord[] = [],
    complianceDocuments: ComplianceDocument[] = [],
    pricingData: PricingDataRecord[] = []
  ): ContractReadinessResult {

    if (!profile) {
      return this.getNotReadyResult('No profile data available');
    }

    const components: ReadinessScoreComponent[] = [];
    const criticalMissing: string[] = [];
    const recommendations: string[] = [];

    // 1. SAM.gov Registration (20%)
    const samScore = this.calculateSamScore(profile);
    components.push(samScore);
    if (samScore.status === 'missing') {
      criticalMissing.push('SAM.gov registration incomplete');
    }

    // 2. Set-Asides (15%)
    const setAsideScore = this.calculateSetAsideScore(profile, setAsideDocuments);
    components.push(setAsideScore);

    // 3. NAICS Codes (15%)
    const naicsScore = this.calculateNaicsScore(profile);
    components.push(naicsScore);
    if (naicsScore.status === 'missing') {
      criticalMissing.push('No NAICS codes specified');
    }

    // 4. Past Performance (15%)
    const ppScore = this.calculatePastPerformanceScore(pastPerformance);
    components.push(ppScore);

    // 5. Key Personnel (10%)
    const personnelScore = this.calculatePersonnelScore(keyPersonnel);
    components.push(personnelScore);

    // 6. Certifications (10%)
    const certScore = this.calculateCertificationScore(profile);
    components.push(certScore);

    // 7. Pricing Data (10%)
    const pricingScore = this.calculatePricingScore(pricingData);
    components.push(pricingScore);

    // 8. Compliance Documents (5%)
    const complianceScore = this.calculateComplianceScore(complianceDocuments);
    components.push(complianceScore);

    // Calculate total weighted score
    const totalScore = components.reduce((sum, c) => sum + (c.score * c.weight / 100), 0);
    const roundedScore = Math.round(totalScore);

    // Determine readiness level
    const readinessLevel = this.getReadinessLevel(roundedScore, criticalMissing.length);

    // Generate recommendations
    components.forEach(c => {
      if (c.status !== 'complete') {
        recommendations.push(...c.actionItems);
      }
    });

    return {
      totalScore: roundedScore,
      readinessLevel,
      components,
      criticalMissing,
      recommendations: recommendations.slice(0, 5), // Top 5 recommendations
      isContractReady: roundedScore >= 60 && criticalMissing.length === 0
    };
  }

  /**
   * SAM.gov Registration Score (20%)
   */
  private static calculateSamScore(profile: Profile): ReadinessScoreComponent {
    let score = 0;
    const actionItems: string[] = [];

    const hasUei = Boolean(profile.uei_number);
    const hasCage = Boolean(profile.cage_code);
    const hasDuns = Boolean(profile.duns_number);
    const samStatus = profile.sam_status?.toLowerCase();
    const isActive = samStatus === 'active';

    if (hasUei) score += 30;
    else actionItems.push('Add your UEI number from SAM.gov');

    if (hasCage) score += 25;
    else actionItems.push('Add your CAGE code');

    if (hasDuns) score += 15;
    else actionItems.push('Add your DUNS number (legacy identifier)');

    if (isActive) score += 30;
    else if (samStatus) actionItems.push('Update SAM.gov registration to Active status');
    else actionItems.push('Set your SAM.gov registration status');

    let status: ReadinessScoreComponent['status'] = 'missing';
    if (score === 100) status = 'complete';
    else if (score >= 55) status = 'partial';
    else if (score > 0) status = 'warning';

    return {
      name: 'SAM.gov Registration',
      score,
      maxScore: 100,
      weight: 20,
      status,
      details: `UEI: ${hasUei ? 'Yes' : 'No'}, CAGE: ${hasCage ? 'Yes' : 'No'}, Status: ${samStatus || 'Not set'}`,
      actionItems
    };
  }

  /**
   * Set-Aside Document Score (15%)
   */
  private static calculateSetAsideScore(
    profile: Profile,
    documents: SetAsideDocument[]
  ): ReadinessScoreComponent {
    const claimedSetAsides = profile.qualified_set_asides || [];
    const actionItems: string[] = [];

    if (claimedSetAsides.length === 0) {
      return {
        name: 'Set-Aside Qualifications',
        score: 50, // Neutral - not everyone needs set-asides
        maxScore: 100,
        weight: 15,
        status: 'partial',
        details: 'No set-asides claimed (OK for full & open competition)',
        actionItems: ['Consider adding set-aside qualifications if eligible (8(a), SDVOSB, WOSB, HUBZone)']
      };
    }

    // Check document uploads for each claimed set-aside
    let documentedCount = 0;
    let expiredCount = 0;

    claimedSetAsides.forEach(setAside => {
      const doc = documents.find(d => d.set_aside_type === setAside);
      if (doc && doc.document_url) {
        // Check if expired
        if (doc.expiration_date && new Date(doc.expiration_date) < new Date()) {
          expiredCount++;
          actionItems.push(`Renew expired ${setAside} certification`);
        } else {
          documentedCount++;
        }
      } else {
        actionItems.push(`Upload certification document for ${setAside}`);
      }
    });

    const totalClaimed = claimedSetAsides.length;
    const documentRate = documentedCount / totalClaimed;
    const score = Math.round(documentRate * 100);

    let status: ReadinessScoreComponent['status'] = 'missing';
    if (score === 100) status = 'complete';
    else if (score >= 50) status = 'partial';
    else if (expiredCount > 0) status = 'warning';

    return {
      name: 'Set-Aside Qualifications',
      score,
      maxScore: 100,
      weight: 15,
      status,
      details: `${documentedCount}/${totalClaimed} documented${expiredCount > 0 ? `, ${expiredCount} expired` : ''}`,
      actionItems
    };
  }

  /**
   * NAICS Codes Score (15%)
   */
  private static calculateNaicsScore(profile: Profile): ReadinessScoreComponent {
    const naicsCodes = profile.naics_codes || [];
    const actionItems: string[] = [];
    let score = 0;

    if (naicsCodes.length === 0) {
      return {
        name: 'NAICS Codes',
        score: 0,
        maxScore: 100,
        weight: 15,
        status: 'missing',
        details: 'No NAICS codes specified',
        actionItems: ['Add your primary NAICS codes to match with relevant contracts']
      };
    }

    // Score based on number of codes (sweet spot is 3-7)
    if (naicsCodes.length >= 3) score += 50;
    else if (naicsCodes.length >= 1) score += 30;

    // Bonus for code diversity (different 2-digit sectors)
    const sectors = new Set(naicsCodes.map(c => c.slice(0, 2)));
    if (sectors.size >= 2) score += 25;
    else score += 10;

    // Bonus for 6-digit specificity
    const hasSpecific = naicsCodes.some(c => c.length >= 6);
    if (hasSpecific) score += 25;
    else actionItems.push('Use 6-digit NAICS codes for better contract matching');

    score = Math.min(score, 100);

    let status: ReadinessScoreComponent['status'] = 'partial';
    if (score >= 80) status = 'complete';
    else if (score < 50) status = 'warning';

    if (naicsCodes.length < 3) {
      actionItems.push('Add more NAICS codes to increase contract opportunities');
    }

    return {
      name: 'NAICS Codes',
      score,
      maxScore: 100,
      weight: 15,
      status,
      details: `${naicsCodes.length} NAICS codes across ${sectors.size} sector(s)`,
      actionItems
    };
  }

  /**
   * Past Performance Score (15%)
   */
  private static calculatePastPerformanceScore(records: PastPerformanceRecord[]): ReadinessScoreComponent {
    const actionItems: string[] = [];

    if (records.length === 0) {
      return {
        name: 'Past Performance',
        score: 0,
        maxScore: 100,
        weight: 15,
        status: 'missing',
        details: 'No past performance records',
        actionItems: ['Add at least 3 relevant past performance records', 'Include government contracts if available']
      };
    }

    let score = 0;

    // Number of records (up to 40 points)
    const recordScore = Math.min(records.length / 5, 1) * 40;
    score += recordScore;

    // Government contracts (20 points)
    const govRecords = records.filter(r => r.sector_type === 'Government');
    if (govRecords.length >= 3) score += 20;
    else if (govRecords.length >= 1) score += 10;
    else actionItems.push('Add government contract experience');

    // Contract numbers tracked (15 points)
    const withContractNum = records.filter(r => r.contract_number);
    if (withContractNum.length >= 2) score += 15;
    else if (withContractNum.length >= 1) score += 8;
    else actionItems.push('Add contract numbers (PIID) to past performance records');

    // CPARS ratings (25 points)
    const withCpars = records.filter(r => r.cpars_overall_rating);
    if (withCpars.length >= 2) score += 25;
    else if (withCpars.length >= 1) score += 15;
    else if (govRecords.length > 0) actionItems.push('Add CPARS ratings for government contracts');

    score = Math.min(score, 100);

    let status: ReadinessScoreComponent['status'] = 'partial';
    if (score >= 80) status = 'complete';
    else if (score < 40) status = 'warning';
    else if (score === 0) status = 'missing';

    return {
      name: 'Past Performance',
      score: Math.round(score),
      maxScore: 100,
      weight: 15,
      status,
      details: `${records.length} records (${govRecords.length} government), ${withCpars.length} with CPARS`,
      actionItems
    };
  }

  /**
   * Key Personnel Score (10%)
   */
  private static calculatePersonnelScore(personnel: KeyPersonnelRecord[]): ReadinessScoreComponent {
    const actionItems: string[] = [];

    if (personnel.length === 0) {
      return {
        name: 'Key Personnel',
        score: 20,
        maxScore: 100,
        weight: 10,
        status: 'warning',
        details: 'No key personnel added',
        actionItems: ['Add key personnel with their qualifications', 'Include security clearance information']
      };
    }

    let score = 0;

    // Team size (40 points)
    if (personnel.length >= 5) score += 40;
    else if (personnel.length >= 3) score += 30;
    else score += personnel.length * 10;

    // Security clearances (35 points)
    const withClearance = personnel.filter(p =>
      p.security_clearance &&
      p.security_clearance.toLowerCase() !== 'none' &&
      p.security_clearance.toLowerCase() !== 'n/a'
    );
    if (withClearance.length >= 3) score += 35;
    else if (withClearance.length >= 1) score += 20;
    else actionItems.push('Add security clearance information for personnel');

    // Certifications (25 points)
    const totalCerts = personnel.reduce((sum, p) => sum + (p.certifications?.length || 0), 0);
    if (totalCerts >= 10) score += 25;
    else if (totalCerts >= 5) score += 15;
    else if (totalCerts >= 1) score += 8;
    else actionItems.push('Add professional certifications for key personnel');

    score = Math.min(score, 100);

    let status: ReadinessScoreComponent['status'] = 'partial';
    if (score >= 80) status = 'complete';
    else if (score < 40) status = 'warning';

    return {
      name: 'Key Personnel',
      score: Math.round(score),
      maxScore: 100,
      weight: 10,
      status,
      details: `${personnel.length} personnel, ${withClearance.length} with clearances, ${totalCerts} certifications`,
      actionItems
    };
  }

  /**
   * Certifications Score (10%)
   */
  private static calculateCertificationScore(profile: Profile): ReadinessScoreComponent {
    const certs = profile.certifications || [];
    const actionItems: string[] = [];

    if (certs.length === 0) {
      return {
        name: 'Company Certifications',
        score: 30,
        maxScore: 100,
        weight: 10,
        status: 'warning',
        details: 'No company certifications listed',
        actionItems: ['Add relevant industry certifications (ISO, CMMI, etc.)']
      };
    }

    let score = 0;

    // Number of certifications
    if (certs.length >= 5) score += 50;
    else if (certs.length >= 3) score += 40;
    else score += certs.length * 15;

    // Check for common federal certifications
    const certText = certs.join(' ').toLowerCase();
    const federalCerts = ['iso', 'cmmi', 'fedramp', 'fisma', 'nist', 'sox', 'pci'];
    const hasFederalCerts = federalCerts.some(fc => certText.includes(fc));

    if (hasFederalCerts) score += 50;
    else actionItems.push('Consider obtaining ISO 9001, CMMI, or other federal-recognized certifications');

    score = Math.min(score, 100);

    let status: ReadinessScoreComponent['status'] = 'partial';
    if (score >= 80) status = 'complete';
    else if (score < 40) status = 'warning';

    return {
      name: 'Company Certifications',
      score: Math.round(score),
      maxScore: 100,
      weight: 10,
      status,
      details: `${certs.length} certifications listed`,
      actionItems
    };
  }

  /**
   * Pricing Data Score (10%)
   */
  private static calculatePricingScore(pricingData: PricingDataRecord[]): ReadinessScoreComponent {
    const actionItems: string[] = [];

    if (pricingData.length === 0) {
      return {
        name: 'Pricing Data',
        score: 20,
        maxScore: 100,
        weight: 10,
        status: 'warning',
        details: 'No pricing data configured',
        actionItems: ['Set up labor rates and cost categories', 'Add pricing for your primary NAICS codes']
      };
    }

    let score = 0;

    // Number of pricing entries
    if (pricingData.length >= 3) score += 50;
    else if (pricingData.length >= 1) score += 30;

    // NAICS coverage
    const naicsCoverage = pricingData.reduce((acc, p) => {
      (p.naics_codes || []).forEach(n => acc.add(n));
      return acc;
    }, new Set<string>());

    if (naicsCoverage.size >= 3) score += 50;
    else if (naicsCoverage.size >= 1) score += 30;
    else actionItems.push('Link pricing data to specific NAICS codes');

    score = Math.min(score, 100);

    let status: ReadinessScoreComponent['status'] = 'partial';
    if (score >= 80) status = 'complete';
    else if (score < 40) status = 'warning';

    return {
      name: 'Pricing Data',
      score: Math.round(score),
      maxScore: 100,
      weight: 10,
      status,
      details: `${pricingData.length} pricing configurations covering ${naicsCoverage.size} NAICS codes`,
      actionItems
    };
  }

  /**
   * Compliance Documents Score (5%)
   */
  private static calculateComplianceScore(documents: ComplianceDocument[]): ReadinessScoreComponent {
    const actionItems: string[] = [];

    if (documents.length === 0) {
      return {
        name: 'Compliance Documents',
        score: 30,
        maxScore: 100,
        weight: 5,
        status: 'warning',
        details: 'No compliance documents uploaded',
        actionItems: ['Upload business licenses and insurance certificates']
      };
    }

    let score = 0;

    // Number of documents
    if (documents.length >= 5) score += 40;
    else if (documents.length >= 3) score += 30;
    else score += documents.length * 10;

    // Check for expired documents
    const expiredDocs = documents.filter(d => d.status === 'expired');
    const expiringDocs = documents.filter(d => d.status === 'expiring_soon');

    if (expiredDocs.length > 0) {
      score -= 20;
      actionItems.push(`${expiredDocs.length} document(s) have expired - renew immediately`);
    }

    if (expiringDocs.length > 0) {
      actionItems.push(`${expiringDocs.length} document(s) expiring soon - plan for renewal`);
    }

    // Check for essential document types
    const docTypes = documents.map(d => d.document_type?.toLowerCase() || '');
    const hasInsurance = docTypes.some(t => t.includes('insurance'));
    const hasLicense = docTypes.some(t => t.includes('license'));

    if (hasInsurance) score += 30;
    else actionItems.push('Upload insurance certificate');

    if (hasLicense) score += 30;
    else actionItems.push('Upload business license');

    score = Math.max(0, Math.min(score, 100));

    let status: ReadinessScoreComponent['status'] = 'partial';
    if (score >= 80 && expiredDocs.length === 0) status = 'complete';
    else if (expiredDocs.length > 0) status = 'warning';
    else if (score < 40) status = 'warning';

    return {
      name: 'Compliance Documents',
      score: Math.round(score),
      maxScore: 100,
      weight: 5,
      status,
      details: `${documents.length} documents (${expiredDocs.length} expired, ${expiringDocs.length} expiring soon)`,
      actionItems
    };
  }

  /**
   * Get readiness level based on score
   */
  private static getReadinessLevel(
    score: number,
    criticalCount: number
  ): ContractReadinessResult['readinessLevel'] {
    if (criticalCount > 0) return 'Not Ready';
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    if (score >= 30) return 'Poor';
    return 'Not Ready';
  }

  /**
   * Return a not-ready result with a message
   */
  private static getNotReadyResult(message: string): ContractReadinessResult {
    return {
      totalScore: 0,
      readinessLevel: 'Not Ready',
      components: [],
      criticalMissing: [message],
      recommendations: ['Complete your company profile to get started'],
      isContractReady: false
    };
  }
}
